import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { config } from '../../config';
import Spinner from '../Global/Spinner';

const API_URL = config.API_URI;

const EVENT_GROUPS = [
  {
    label: 'Card',
    events: [
      { id: 'card.created', label: 'Created' },
      { id: 'card.updated', label: 'Updated' },
      { id: 'card.deleted', label: 'Deleted' },
      { id: 'card.moved', label: 'Moved' },
      { id: 'card.archived', label: 'Archived' },
      { id: 'card.member_added', label: 'Member added' },
      { id: 'card.member_removed', label: 'Member removed' },
      { id: 'card.comment_added', label: 'Comment added' },
    ],
  },
  {
    label: 'Board',
    events: [
      { id: 'board.created', label: 'Created' },
      { id: 'board.updated', label: 'Updated' },
      { id: 'board.deleted', label: 'Deleted' },
      { id: 'board.archived', label: 'Archived' },
      { id: 'board.member_added', label: 'Member added' },
      { id: 'board.member_removed', label: 'Member removed' },
    ],
  },
  {
    label: 'List',
    events: [
      { id: 'list.created', label: 'Created' },
      { id: 'list.updated', label: 'Updated' },
      { id: 'list.deleted', label: 'Deleted' },
    ],
  },
  {
    label: 'Page',
    events: [
      { id: 'page.created', label: 'Created' },
      { id: 'page.updated', label: 'Updated' },
      { id: 'page.deleted', label: 'Deleted' },
    ],
  },
  {
    label: 'Workspace',
    events: [
      { id: 'workspace.member_added', label: 'Member added' },
      { id: 'workspace.member_removed', label: 'Member removed' },
    ],
  },
];

const ALL_EVENTS = EVENT_GROUPS.flatMap(g => g.events.map(e => e.id));

const WebhooksSection = ({ workspace }) => {
  const dispatch = useDispatch();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([...ALL_EVENTS]);
  const [generatedSecret, setGeneratedSecret] = useState('');
  const [copied, setCopied] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    if (!workspace?.shortId) return;
    try {
      const response = await fetch(
        `${API_URL}/v1/webhooks?workspaceId=${workspace.shortId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
    } finally {
      setLoading(false);
    }
  }, [workspace?.shortId]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleCreate = async () => {
    if (!url.trim()) {
      dispatch(showSnackBar({ message: 'Please enter a webhook URL', type: 'error' }));
      return;
    }
    try {
      new URL(url);
      if (!url.startsWith('https://')) {
        dispatch(showSnackBar({ message: 'URL must use HTTPS', type: 'error' }));
        return;
      }
    } catch {
      dispatch(showSnackBar({ message: 'Please enter a valid URL', type: 'error' }));
      return;
    }
    if (selectedEvents.length === 0) {
      dispatch(showSnackBar({ message: 'Select at least one event', type: 'error' }));
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/v1/webhooks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: url.trim(),
          description: description.trim(),
          events: selectedEvents,
          workspaceId: workspace.shortId,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create webhook');

      setGeneratedSecret(data.data.secret);
      dispatch(showSnackBar({ message: 'Webhook created', type: 'success' }));
      fetchWebhooks();
    } catch (err) {
      dispatch(showSnackBar({ message: err.message, type: 'error' }));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (webhookId) => {
    try {
      const response = await fetch(`${API_URL}/v1/webhooks/${webhookId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        dispatch(showSnackBar({ message: 'Webhook deleted', type: 'success' }));
        fetchWebhooks();
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to delete webhook', type: 'error' }));
    }
  };

  const handleTest = async (webhookId) => {
    try {
      const response = await fetch(`${API_URL}/v1/webhooks/${webhookId}/test`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        dispatch(showSnackBar({ message: `Ping sent — ${data.statusCode} (${data.duration}ms)`, type: 'success' }));
      } else {
        dispatch(showSnackBar({ message: `Ping failed: ${data.error || data.statusCode}`, type: 'error' }));
      }
      fetchWebhooks();
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to send test ping', type: 'error' }));
    }
  };

  const handleToggle = async (webhook) => {
    try {
      const response = await fetch(`${API_URL}/v1/webhooks/${webhook._id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !webhook.isActive }),
      });
      if (response.ok) {
        dispatch(showSnackBar({
          message: webhook.isActive ? 'Webhook disabled' : 'Webhook enabled',
          type: 'success',
        }));
        fetchWebhooks();
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to update webhook', type: 'error' }));
    }
  };

  const toggleEvent = (eventId) => {
    setSelectedEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(e => e !== eventId)
        : [...prev, eventId]
    );
  };

  const toggleGroup = (group) => {
    const groupEventIds = group.events.map(e => e.id);
    const allSelected = groupEventIds.every(id => selectedEvents.includes(id));
    if (allSelected) {
      setSelectedEvents(prev => prev.filter(e => !groupEventIds.includes(e)));
    } else {
      setSelectedEvents(prev => [...new Set([...prev, ...groupEventIds])]);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setShowCreate(false);
    setGeneratedSecret('');
    setUrl('');
    setDescription('');
    setSelectedEvents([...ALL_EVENTS]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const getLastDelivery = (webhook) => {
    if (!webhook.deliveryLog || webhook.deliveryLog.length === 0) return null;
    return webhook.deliveryLog[webhook.deliveryLog.length - 1];
  };

  if (loading) return <div className="setting-row"><Spinner /></div>;

  return (
    <div className="webhooks-section">
      <div className="setting-row">
        <div className="setting-label">
          <h2>Webhooks</h2>
          <p>Receive HTTP POST notifications when events happen in your workspace.</p>
        </div>
        <div className="webhooks-content">
          {/* Generated secret display */}
          {generatedSecret && (
            <div className="webhook-secret-display">
              <div className="webhook-secret-warning">
                Save this signing secret now — you won't see it again.
              </div>
              <div className="webhook-secret-value">
                <code className="webhook-secret-code">{generatedSecret}</code>
                <button
                  className="webhook-copy-btn"
                  onClick={() => copyToClipboard(generatedSecret)}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="webhook-secret-hints">
                <div className="secret-hint">
                  <strong>Verify payloads</strong> using the <code>X-Zoobbe-Signature</code> header
                  with HMAC-SHA256.
                </div>
                <div className="secret-hint">
                  <strong>Format:</strong> <code>t=timestamp,v1=signature</code>
                </div>
              </div>
              <button className="webhook-done-btn" onClick={resetForm}>Done</button>
            </div>
          )}

          {/* Create form */}
          {showCreate && !generatedSecret && (
            <div className="webhook-create-form">
              <div className="form-field">
                <label>Payload URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/webhooks/zoobbe"
                  className="webhook-input"
                />
              </div>
              <div className="form-field">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Slack notifications, CI trigger"
                  className="webhook-input"
                  maxLength={200}
                />
              </div>
              <div className="form-field">
                <label>
                  Events
                  <button
                    className="select-all-link"
                    onClick={() => setSelectedEvents(
                      selectedEvents.length === ALL_EVENTS.length ? [] : [...ALL_EVENTS]
                    )}
                  >
                    {selectedEvents.length === ALL_EVENTS.length ? 'Deselect all' : 'Select all'}
                  </button>
                </label>
                <div className="webhook-events-groups">
                  {EVENT_GROUPS.map(group => {
                    const groupEventIds = group.events.map(e => e.id);
                    const allGroupSelected = groupEventIds.every(id => selectedEvents.includes(id));
                    return (
                      <div key={group.label} className="event-group">
                        <label className="event-group-header" onClick={() => toggleGroup(group)}>
                          <input
                            type="checkbox"
                            checked={allGroupSelected}
                            onChange={() => toggleGroup(group)}
                          />
                          <span>{group.label}</span>
                        </label>
                        <div className="event-group-items">
                          {group.events.map(event => (
                            <label key={event.id} className="event-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedEvents.includes(event.id)}
                                onChange={() => toggleEvent(event.id)}
                              />
                              <span>{event.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="webhook-create-btn"
                  onClick={handleCreate}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Webhook'}
                </button>
                <button className="webhook-cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Webhooks list */}
          {!showCreate && !generatedSecret && (
            <>
              <button
                className="webhook-add-btn"
                onClick={() => setShowCreate(true)}
              >
                + Add Webhook
              </button>

              {webhooks.length > 0 ? (
                <div className="webhooks-list">
                  {webhooks.map(webhook => {
                    const lastDelivery = getLastDelivery(webhook);
                    const isExpanded = expandedLog === webhook._id;
                    return (
                      <div key={webhook._id} className={`webhook-item ${!webhook.isActive ? 'disabled' : ''}`}>
                        <div className="webhook-item-main">
                          <div className="webhook-info">
                            <div className="webhook-url">{webhook.url}</div>
                            <div className="webhook-meta">
                              <span className={`webhook-status-badge ${webhook.isActive ? 'active' : 'inactive'}`}>
                                {webhook.isActive ? 'Active' : 'Disabled'}
                              </span>
                              <span className="webhook-separator">·</span>
                              <span>{webhook.events?.length || 0} events</span>
                              {webhook.consecutiveFailures > 0 && (
                                <>
                                  <span className="webhook-separator">·</span>
                                  <span className="webhook-failures">
                                    {webhook.consecutiveFailures} failures
                                  </span>
                                </>
                              )}
                              {lastDelivery && (
                                <>
                                  <span className="webhook-separator">·</span>
                                  <span>
                                    Last: {lastDelivery.success ? (
                                      <span className="delivery-success">{lastDelivery.statusCode}</span>
                                    ) : (
                                      <span className="delivery-fail">Failed</span>
                                    )}
                                  </span>
                                </>
                              )}
                              {webhook.description && (
                                <>
                                  <span className="webhook-separator">·</span>
                                  <span>{webhook.description}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="webhook-actions">
                            <button
                              className="webhook-test-btn"
                              onClick={() => handleTest(webhook._id)}
                            >
                              Ping
                            </button>
                            <button
                              className="webhook-log-btn"
                              onClick={() => setExpandedLog(isExpanded ? null : webhook._id)}
                            >
                              {isExpanded ? 'Hide logs' : 'Logs'}
                            </button>
                            <button
                              className="webhook-toggle-btn"
                              onClick={() => handleToggle(webhook)}
                            >
                              {webhook.isActive ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              className="webhook-delete-btn"
                              onClick={() => handleDelete(webhook._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="webhook-delivery-log">
                            {webhook.deliveryLog && webhook.deliveryLog.length > 0 ? (
                              <table className="delivery-log-table">
                                <thead>
                                  <tr>
                                    <th>Event</th>
                                    <th>Status</th>
                                    <th>Duration</th>
                                    <th>Time</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...webhook.deliveryLog].reverse().map((log, i) => (
                                    <tr key={i} className={log.success ? 'log-success' : 'log-fail'}>
                                      <td><code>{log.event}</code></td>
                                      <td>
                                        {log.success ? (
                                          <span className="delivery-success">{log.statusCode}</span>
                                        ) : (
                                          <span className="delivery-fail">
                                            {log.error || `${log.statusCode}`}
                                          </span>
                                        )}
                                      </td>
                                      <td>{log.duration ? `${log.duration}ms` : '—'}</td>
                                      <td>{formatDate(log.deliveredAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="delivery-log-empty">No deliveries yet</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="webhooks-empty">
                  No webhooks configured. Add one to receive event notifications.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebhooksSection;
